import { FormGroup, InputGroup } from '@clarion-blueprint-shim';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ClarionButton } from '../clarion/ClarionButton';
import { ClarionDialog } from '../clarion/ClarionDialog';

// These functions allow showing app-themed dialogs from anywhere in the app,
// without requiring local state or changing what is rendered (e.g., from event handlers).

export async function showConfirm(
  message: string,
  confirmButton = 'OK',
  cancelButton = 'Cancel',
  destructive = false,
): Promise<boolean> {
  return new Promise((resolve) => {
    const dialogRoot = document.createElement('div');
    document.body.appendChild(dialogRoot);
    const root = createRoot(dialogRoot);

    const onFinish = (result: boolean) => {
      root.unmount();
      document.body.removeChild(dialogRoot);
      resolve(result);
    };
    const onCancel = () => onFinish(false);
    const onSubmit = () => onFinish(true);

    root.render(
      <PromptDialog
        {...{ message, confirmButton, cancelButton, destructive, onCancel, onSubmit }}
      />,
    );
  });
}

export async function showPrompt(
  message: string,
  defaultValue = '',
  dialogProps?: ShowPromptOptions,
): Promise<string | null> {
  return new Promise((resolve) => {
    const dialogRoot = document.createElement('div');
    document.body.appendChild(dialogRoot);
    const root = createRoot(dialogRoot);

    const onSubmit = (result: string | null) => {
      root.unmount();
      document.body.removeChild(dialogRoot);
      resolve(result);
    };

    const onCancel = () => onSubmit(null);

    root.render(
      <PromptDialog showInput {...{ message, defaultValue, onSubmit, onCancel, ...dialogProps }} />,
    );
  });
}

type ShowPromptOptions = {
  confirmButton?: string;
  cancelButton?: string;
  destructive?: boolean;
  maxLength?: number;
};

type IPromptDialogProps = ShowPromptOptions & {
  message: string;
  defaultValue?: string;
  showInput?: boolean;
  onSubmit: (result: string) => void;
  onCancel: () => void;
};

function PromptDialog({
  message,
  defaultValue = '',
  showInput = false,
  onSubmit,
  onCancel,
  confirmButton = 'OK',
  cancelButton = 'Cancel',
  destructive = false,
  maxLength,
}: IPromptDialogProps) {
  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    if (showInput) {
      const input = document.getElementById('promptInput') as HTMLInputElement | null;
      input?.focus?.();
    }
  }, [showInput]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(value);
  };

  return (
    <ClarionDialog
      isOpen
      onClose={onCancel}
      isDismissable={false}
      aria-label={message}
      style={{ width: 'min(500px, 100vw)' }}
    >
      <form onSubmit={handleSubmit}>
        {showInput ? (
          <FormGroup label={message} labelFor="promptInput">
            <InputGroup
              id="promptInput"
              autoComplete="off"
              value={value}
              onChange={setValue}
              style={{ marginTop: '8px' }}
              isRequired
              maxLength={maxLength}
            />
          </FormGroup>
        ) : (
          message
        )}
        <div className="flex gap-2 items-center mt-4 justify-end">
          <ClarionButton type="button" onClick={onCancel}>
            {cancelButton}
          </ClarionButton>
          <ClarionButton intent={destructive ? 'danger' : 'primary'} type="submit">
            {confirmButton}
          </ClarionButton>
        </div>
      </form>
    </ClarionDialog>
  );
}
